<template>
  <v-dialog max-width="1100px" v-model="showDialog" persistent>
    <v-card>
      <!-- Header -->
      <v-card-title class="secondary white--text py-3 px-5">
        <v-icon color="white" class="mr-2">mdi-file-plus-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Create DA</span>
        <v-spacer />
        <v-btn icon color="white" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Order Info Strip -->
      <div
        class="d-flex align-center flex-wrap px-5 py-2"
        style="background: #f8f9fa; border-bottom: 1px solid #e9ecef; gap: 0"
      >
        <div v-if="orderInfo.eToken" class="info-chip">
          <span class="info-label">Token</span>
          <span class="info-value">{{ orderInfo.eToken }}</span>
        </div>
        <div v-if="orderInfo.sourceLocation" class="info-chip">
          <span class="info-label">Source</span>
          <span class="info-value">{{ orderInfo.sourceLocation }}</span>
        </div>
        <div v-if="orderInfo.laneName" class="info-chip">
          <span class="info-label">Destination</span>
          <span class="info-value">{{ orderInfo.laneName }}</span>
        </div>
        <div v-if="orderInfo.pickupArrival" class="info-chip">
          <span class="info-label">Gate-In</span>
          <span class="info-value">{{ orderInfo.pickupArrival }}</span>
        </div>
        <div v-if="orderInfo.pickupDeparture" class="info-chip">
          <span class="info-label">Gate-Out</span>
          <span class="info-value">{{ orderInfo.pickupDeparture }}</span>
        </div>
        <div v-if="orderInfo.dropArrival" class="info-chip">
          <span class="info-label">Drop Gate-In</span>
          <span class="info-value">{{ orderInfo.dropArrival }}</span>
        </div>
        <div v-if="orderInfo.dropDeparture" class="info-chip">
          <span class="info-label">Drop Gate-Out</span>
          <span class="info-value">{{ orderInfo.dropDeparture }}</span>
        </div>
      </div>

      <v-card-text class="px-6 pt-5 pb-2" style="min-height: 420px">
        <!-- Contract Status -->
        <div
          v-if="contractChecked && !hasContract"
          class="contract-status contract-status--warn mb-4"
        >
          <v-icon small class="mr-2">mdi-alert-outline</v-icon>
          <span
            >No active contract found. Update the fields to find a valid
            contract before confirming.</span
          >
        </div>
        <div
          v-if="contractChecked && hasContract"
          class="contract-status contract-status--found mb-4"
        >
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-check-circle-outline</v-icon>
            <span class="font-weight-medium"
              >Contract found — {{ contractIdDisplay }}</span
            >
          </div>
          <div
            v-if="contractRates.length"
            class="d-flex flex-wrap mt-2"
            style="gap: 6px"
          >
            <span
              v-for="(r, i) in contractRates"
              :key="i"
              :class="['rate-chip', !r.has_rate ? 'rate-chip--missing' : '']"
              >{{ r.container_type_name }}:
              {{ r.has_rate ? "AED " + r.rate : "No rate" }}</span
            >
          </div>
        </div>
        <div
          v-if="contractChecked && form.is_sub_hire && hasVendorContract"
          class="contract-status contract-status--found mb-4"
        >
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-check-circle-outline</v-icon>
            <span class="font-weight-medium">Transporter contract found</span>
          </div>
          <div
            v-if="vendorRates.length"
            class="d-flex flex-wrap mt-2"
            style="gap: 6px"
          >
            <span
              v-for="(r, i) in vendorRates"
              :key="i"
              class="rate-chip"
              >{{ r.container_type_name }}: AED {{ r.rate }}</span
            >
          </div>
        </div>
        <div
          v-if="contractChecked && form.is_sub_hire && !hasVendorContract"
          class="contract-status contract-status--warn mb-4"
        >
          <v-icon small class="mr-2">mdi-alert-outline</v-icon>
          <span>No active transporter contract found for this vendor.</span>
        </div>

        <!-- Customer & Lane -->
        <v-row dense>
          <v-col cols="6">
            <Autocomplete
              label="Select Customer *"
              :items="customerList"
              itemText="company_name"
              itemValue="id"
              :loading="customerLoading"
              :totalItems="customerCount"
              @updateList="fetchCustomers"
              hide-details="auto"
              v-model="form.customer"
              @input="onContractFieldChange"
            />
          </v-col>
          <v-col cols="6">
            <Autocomplete
              label="Destination Lane"
              :items="laneList"
              itemText="lane_title"
              itemValue="id"
              :loading="laneLoading"
              :totalItems="laneCount"
              @updateList="fetchLanes"
              hide-details="auto"
              v-model="form.lane"
              @input="onLaneChange"
            />
          </v-col>
        </v-row>

        <!-- Containers Section -->
        <div class="section-block mt-4">
          <div class="section-header d-flex align-center mb-3">
            <v-icon small color="primary" class="mr-1"
              >mdi-package-variant</v-icon
            >
            <span
              class="text-caption font-weight-bold text-uppercase primary--text"
            >
              Actual Containers <span class="grey--text">(Max 4)</span>
            </span>
            <v-spacer />
            <v-btn
              v-if="form.containers.length < 4"
              text
              x-small
              color="primary"
              @click="addContainer"
            >
              <v-icon x-small class="mr-1">mdi-plus-circle-outline</v-icon>
              Add Container
            </v-btn>
          </div>

          <div
            v-for="(container, index) in form.containers"
            :key="index"
            class="container-row d-flex align-center flex-wrap mb-2"
          >
            <div class="container-index mr-2">
              <v-avatar size="20" color="primary lighten-4">
                <span class="text-caption primary--text font-weight-bold">{{
                  index + 1
                }}</span>
              </v-avatar>
            </div>
            <v-text-field
              dense
              outlined
              hide-details="auto"
              label="Container No"
              v-model="container.container_no"
              class="mr-2"
              style="max-width: 240px"
            />
            <MultiSelect
              label="Container Type"
              :items="containerTypeList"
              itemText="type_name"
              itemValue="id"
              hide-details="auto"
              outlined
              dense
              clearable
              v-model="container.container_type"
              style="min-width: 260px"
              @input="index === 0 ? onContainerTypeChange() : null"
            />
            <div v-if="container.origin || container.destination" class="d-flex align-center ml-3" style="white-space:nowrap; font-size:12px; color:#616161; gap:4px;">
              <v-icon x-small color="grey darken-1">mdi-map-marker-outline</v-icon>
              <span>{{ container.origin || '—' }}</span>
              <v-icon x-small color="grey">mdi-arrow-right</v-icon>
              <span style="font-weight:500; color:#e65100;">{{ container.destination || '—' }}</span>
            </div>
            <v-btn
              v-if="form.containers.length > 1"
              icon
              x-small
              color="error"
              class="ml-1"
              @click="removeContainer(index)"
            >
              <v-icon small>mdi-close-circle</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Execution Assignment Section -->
        <div class="section-block mt-4">
          <div class="section-header d-flex align-center mb-3">
            <v-icon small color="primary" class="mr-1"
              >mdi-truck-outline</v-icon
            >
            <span
              class="text-caption font-weight-bold text-uppercase primary--text"
            >
              Execution Assignment
            </span>
          </div>

          <v-checkbox
            v-model="form.is_sub_hire"
            label="Is this a Sub-Hire execution?"
            hide-details="auto"
            dense
            class="mt-0 mb-3"
            @change="onSubHireChange"
          />

          <v-row dense v-if="form.is_sub_hire">
            <v-col cols="12">
              <Autocomplete
                label="Select Vendor *"
                :items="vendorList"
                itemText="company_name"
                itemValue="id"
                :loading="vendorLoading"
                :totalItems="vendorCount"
                @updateList="fetchVendors"
                hide-details="auto"
                v-model="form.vendor"
                @input="onContractFieldChange"
              />
            </v-col>
          </v-row>

          <v-row dense v-else>
            <v-col cols="6">
              <Autocomplete
                label="Vehicle Registration"
                :items="vehicleList"
                itemText="vehicle_number"
                itemValue="id"
                :loading="vehicleLoading"
                :totalItems="vehicleCount"
                @updateList="fetchVehicles"
                hide-details="auto"
                v-model="form.vehicle"
                @input="onVehicleChange"
              />
            </v-col>
            <v-col cols="6">
              <Autocomplete
                label="Driver Name"
                :items="driverList"
                itemText="driver_name"
                itemValue="id"
                :loading="driverLoading"
                :totalItems="driverCount"
                @updateList="fetchDrivers"
                hide-details="auto"
                v-model="form.driver"
                @input="onDriverChange"
              />
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="closeDialog" class="mr-2"
          >Cancel</v-btn
        >
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="
            !isFormValid || !hasContract || (contractChecked && !allRatesValid)
          "
          min-width="120"
          @click="submit"
        >
          <v-icon small class="mr-1">mdi-check-circle</v-icon>
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

export default {
  name: "CreateDADialog",
  components: { Autocomplete, MultiSelect },
  props: {
    value: { type: Boolean, default: false },
    orderId: { type: Number, default: null },
    sourceLocation: { type: Number, default: null },
    orderInfo: { type: Object, default: () => ({}) },
    containerOverride: { type: Array, default: null },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this.fetchInitialData();
        this.prefillFromOrder();
      }
    },
  },
  data() {
    return {
      form: {
        customer: null,
        lane: null,
        is_sub_hire: false,
        vendor: null,
        vehicle: null,
        vehicle_number: null,
        driver: null,
        driver_name: null,
        containers: [{ container_no: "", container_type: null }],
      },
      hasContract: false,
      allRatesValid: false,
      contractChecked: false,
      contractChecking: false,
      contractIdDisplay: null,
      contractRates: [],
      hasVendorContract: false,
      vendorRates: [],
      customerList: [],
      customerCount: 0,
      customerLoading: false,
      laneList: [],
      laneCount: 0,
      laneLoading: false,
      containerTypeList: [],
      vehicleList: [],
      vehicleCount: 0,
      vehicleLoading: false,
      driverList: [],
      driverCount: 0,
      driverLoading: false,
      vendorList: [],
      vendorCount: 0,
      vendorLoading: false,
      submitting: false,
      _autoPopulating: false,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isFormValid() {
      if (!this.form.customer) return false;
      const hasContainer = this.form.containers.some(
        (c) => c.container_no && c.container_no.trim(),
      );
      if (!hasContainer) return false;
      if (this.form.is_sub_hire) {
        if (!this.form.vendor) return false;
        if (this.contractChecked && !this.hasVendorContract) return false;
        return true;
      }
      return !!this.form.vehicle && !!this.form.driver;
    },
  },
  methods: {
    prefillFromOrder() {
      const o = this.orderInfo;
      if (!o) return;

      if (o.isSubHire !== undefined) {
        this.form.is_sub_hire = !!o.isSubHire;
        if (this.form.is_sub_hire) {
          this.fetchVendors({ limit: 10, offset: 0 });
        }
      }

      // Use containerOverride prop when set (single container from detail page action)
      const raw = this.containerOverride || o.containerData;
      if (Array.isArray(raw) && raw.length) {
        this.form.containers = raw.slice(0, 4).map((c) => ({
          container_no: c.container_no || c.container_number || "",
          container_type: c.container_type || null,
          _typeNameHint: c.type_name || c.container_type_name || null,
          lane_id: c.lane_id || null,
          customer_id: c.customer_id || null,
          origin: c.origin || null,
          destination: c.destination || null,
        }));
      }
    },

    matchContainerTypes() {
      if (!this.containerTypeList.length) return;
      this.form.containers = this.form.containers.map((c) => {
        if (c._typeNameHint && !c.container_type) {
          const match = this.containerTypeList.find(
            (t) => t.type_name === c._typeNameHint,
          );
          return { ...c, container_type: match ? match.id : null };
        }
        return c;
      });
      // After types resolved: auto-resolve customer if lane set but customer still empty
      this.$nextTick(() => {
        const laneId = this.form.lane && typeof this.form.lane === 'object' ? this.form.lane.id : this.form.lane;
        if (laneId && !this.form.customer) {
          this._resolveCustomerFromLane(laneId);
        } else {
          this.checkContract();
        }
      });
    },

    onContractFieldChange() {
      this.contractChecked = false;
      this.hasContract = false;
      this.allRatesValid = false;
      this.contractIdDisplay = null;
      this.contractRates = [];
      this.hasVendorContract = false;
      this.vendorRates = [];
      this.checkContract();
    },

    onContainerTypeChange() {
      this.onContractFieldChange();
      // if lane is already set but customer hasn't been resolved yet, try again now that type is known
      const laneId = this.form.lane && typeof this.form.lane === 'object' ? this.form.lane.id : this.form.lane;
      if (laneId && !this.form.customer) {
        this.onLaneChange(this.form.lane);
      }
    },

    _resolveCustomerFromLane(laneId) {
      const containerTypeId = this.form.containers.map((c) => c.container_type).filter(Boolean)[0] || null;
      const params = { lane_id: laneId };
      if (containerTypeId) params.container_type_id = containerTypeId;
      this.$api.emergencyOrder
        .resolveCustomerByLane(params)
        .then((res) => {
          const data = res.data || res;
          if (data.customer_id) {
            const existing = this.customerList.find((c) => c.id === data.customer_id);
            if (existing) {
              this.form.customer = existing;
            } else {
              const entry = { id: data.customer_id, company_name: data.company_name };
              this.customerList = [entry, ...this.customerList];
              this.form.customer = entry;
            }
            this.checkContract();
          }
        })
        .catch(() => {});
    },

    onLaneChange(val) {
      this.onContractFieldChange();
      const laneId = val && typeof val === 'object' ? val.id : val;
      if (!laneId || this.form.customer) return;
      this._resolveCustomerFromLane(laneId);
    },

    checkContract() {
      const customerId =
        this.form.customer && typeof this.form.customer === "object"
          ? this.form.customer.id
          : this.form.customer;
      const laneId =
        this.form.lane && typeof this.form.lane === "object"
          ? this.form.lane.id
          : this.form.lane;
      const containerTypeId = [...new Set(
        this.form.containers.map((c) => c.container_type).filter(Boolean)
      )];
      const vendorId = this.form.is_sub_hire
        ? this.form.vendor && typeof this.form.vendor === "object"
          ? this.form.vendor.id
          : this.form.vendor
        : null;

      if (!customerId || !laneId) return;

      this.contractChecking = true;
      const params = { customer_id: customerId, lane_id: laneId };
      if (containerTypeId.length)
        params.container_type_id = containerTypeId.join(",");
      if (vendorId) params.vendor_id = vendorId;

      this.$api.emergencyOrder
        .checkEmergencyContract(params)
        .then((res) => {
          const data = res.data || res;
          this.hasContract = data.has_customer_contract;
          this.allRatesValid = data.all_rates_valid || false;
          this.contractIdDisplay = data.contract_id_display || null;
          this.contractRates = data.contract_rates || [];
          this.hasVendorContract = data.has_vendor_contract || false;
          this.vendorRates = data.vendor_rates || [];
          this.contractChecked = true;
        })
        .catch(() => {})
        .finally(() => {
          this.contractChecking = false;
        });
    },

    prefillVehicle() {
      const num = this.orderInfo?.vehicleNumber;
      if (!num) return;
      const match = this.vehicleList.find(
        (v) => v.vehicle_number === num || v.inventory_number === num,
      );
      if (match) {
        this.form.vehicle = match;
        this.form.vehicle_number = match.vehicle_number || match.inventory_number;
        if (!this.form.driver) this.onVehicleChange(match);
      } else {
        this.$api.vehicle
          .getVehicleSelectList({ search: num, limit: 1 })
          .then((res) => {
            if (res.data && res.data.length) {
              const v = res.data[0];
              if (!this.vehicleList.find((x) => x.id === v.id))
                this.vehicleList.push(v);
              this.form.vehicle = v;
              this.form.vehicle_number = v.vehicle_number || v.inventory_number;
              if (!this.form.driver) this.onVehicleChange(v);
            }
          });
      }
    },

    prefillDriver() {
      const name = this.orderInfo?.driverName;
      if (!name) return;
      const match = this.driverList.find((d) => d.driver_name === name);
      if (match) {
        this.form.driver = match;
        this.form.driver_name = match.driver_name;
        if (!this.form.vehicle) this.onDriverChange(match);
      } else {
        this.$api.driver
          .getDriverSelectList({ search: name, limit: 1 })
          .then((res) => {
            if (res.data && res.data.length) {
              const d = res.data[0];
              if (!this.driverList.find((x) => x.id === d.id))
                this.driverList.push(d);
              this.form.driver = d;
              this.form.driver_name = d.driver_name;
              if (!this.form.vehicle) this.onDriverChange(d);
            }
          });
      }
    },

    resetForm() {
      this._autoPopulating = false;
      this.form = {
        customer: null,
        lane: null,
        is_sub_hire: false,
        vendor: null,
        vehicle: null,
        vehicle_number: null,
        driver: null,
        driver_name: null,
        containers: [{ container_no: "", container_type: null }],
      };
      this.hasContract = false;
      this.contractChecked = false;
      this.customerList = [];
      this.laneList = [];
      this.vehicleList = [];
      this.driverList = [];
      this.vendorList = [];
    },

    closeDialog() {
      this.showDialog = false;
    },
    addContainer() {
      if (this.form.containers.length < 4)
        this.form.containers.push({ container_no: "", container_type: null });
    },
    removeContainer(index) {
      this.form.containers.splice(index, 1);
    },

    onSubHireChange() {
      this.form.vendor = null;
      this.form.vehicle = null;
      this.form.vehicle_number = null;
      this.form.driver = null;
      this.form.driver_name = null;
      this.contractChecked = false;
      this.hasContract = false;
      if (this.form.is_sub_hire) {
        this.vendorList = [];
        this.fetchVendors({ limit: 10, offset: 0 });
      }
    },
    onVehicleChange(item) {
      this.form.vehicle_number = item ? (item.vehicle_number || item.inventory_number) : null;
      if (!item || this._autoPopulating) return;
      this._autoPopulating = true;
      const assignedDriver = this.driverList.find(
        (d) => d.assigned_vehicle_id === item.id
      );
      if (assignedDriver) {
        this.form.driver = assignedDriver;
        this.form.driver_name = assignedDriver.driver_name;
        this._autoPopulating = false;
      } else {
        this.$api.driver
          .getDriverSelectList({ assigned_vehicle: item.id, limit: 1 })
          .then((res) => {
            const driver = res.data && res.data[0];
            if (driver && !this.form.driver) {
              this.form.driver = driver;
              this.form.driver_name = driver.driver_name;
              if (!this.driverList.find((d) => d.id === driver.id)) {
                this.driverList = [driver, ...this.driverList];
              }
            }
          })
          .catch(() => {})
          .finally(() => { this._autoPopulating = false; });
      }
    },
    onDriverChange(item) {
      this.form.driver_name = item ? item.driver_name : null;
      if (!item || !item.assigned_vehicle_id || this._autoPopulating) return;
      this._autoPopulating = true;
      const assignedVehicle = this.vehicleList.find(
        (v) => v.id === item.assigned_vehicle_id
      );
      if (assignedVehicle) {
        this.form.vehicle = assignedVehicle;
        this.form.vehicle_number = assignedVehicle.vehicle_number || assignedVehicle.inventory_number;
        this._autoPopulating = false;
      } else {
        this.$api.vehicle
          .getVehicleObject(item.assigned_vehicle_id)
          .then((res) => {
            const vehicle = res.data;
            if (vehicle) {
              this.form.vehicle = vehicle;
              this.form.vehicle_number = vehicle.vehicle_number || vehicle.inventory_number;
              if (!this.vehicleList.find((v) => v.id === vehicle.id)) {
                this.vehicleList = [vehicle, ...this.vehicleList];
              }
            }
          })
          .catch(() => {})
          .finally(() => { this._autoPopulating = false; });
      }
    },

    fetchInitialData() {
      this.fetchCustomers({ limit: 10, offset: 0 });
      this.fetchLanes({ limit: 10, offset: 0 });
      this.fetchContainerTypes();
      this.fetchVehicles({ limit: 10, offset: 0 });
      this.fetchDrivers({ limit: 10, offset: 0 });
    },
    fetchCustomers(params = {}) {
      this.customerLoading = true;
      const query = {
        company_type: "Customer",
        is_emergency_customer: true,
        limit: 10,
        ...params,
      };
      this.$api.company
        .getCompanyList(query)
        .then((res) => {
          this.customerList = params.offset
            ? [...this.customerList, ...res.data]
            : res.data;
          this.customerCount = res.count;
          if (
            !params.offset &&
            this.orderInfo?.customerId &&
            !this.form.customer
          ) {
            const match = this.customerList.find(
              (c) => c.id === this.orderInfo.customerId,
            );
            if (match) {
              this.form.customer = match;
              this.checkContract();
            } else {
              this.$api.company
                .getCompanyObject(this.orderInfo.customerId)
                .then((r) => {
                  if (r.data && r.data.id) {
                    this.customerList = [r.data, ...this.customerList];
                    this.form.customer = r.data;
                    this.checkContract();
                  }
                });
            }
          }
        })
        .finally(() => {
          this.customerLoading = false;
        });
    },
    fetchLanes(params = {}) {
      this.laneLoading = true;
      const query = { limit: 10, ...params };
      if (this.sourceLocation) query.source_location = this.sourceLocation;
      this.$api.lane
        .getLaneList(query)
        .then((res) => {
          this.laneList = params.offset
            ? [...this.laneList, ...res.data]
            : res.data;
          this.laneCount = res.count;
          if (!params.offset && !this.form.lane) {
            const singleOverride = this.containerOverride?.length === 1 ? this.containerOverride[0] : null;
            const destinationStr = singleOverride?.destination || null;
            const originStr = singleOverride?.origin || null;
            const perContainerLaneId = singleOverride?.lane_id || null;

            const _applyLane = (laneObj) => {
              this.laneList = this.laneList.some(l => l.id === laneObj.id)
                ? this.laneList
                : [laneObj, ...this.laneList];
              this.form.lane = laneObj;
              if (!this.form.customer) this._resolveCustomerFromLane(laneObj.id);
              else this.checkContract();
            };

            if (destinationStr) {

              const searchTerm = originStr ? `${originStr}-${destinationStr}` : destinationStr;
              this.$api.lane.getLaneList({
                limit: 5,
                search: searchTerm,
                ...(this.sourceLocation ? { source_location: this.sourceLocation } : {}),
              }).then((res) => {
                const lanes = res.data || [];
                const exactTitle = searchTerm.toLowerCase();
                const found = (
                  lanes.find(l => (l.lane_title || '').toLowerCase() === exactTitle) ||
                  lanes.find(l => (l.lane_title || '').toLowerCase().startsWith(exactTitle)) ||
                  lanes[0]
                );
                if (found) {
                  _applyLane(found);
                } else if (perContainerLaneId) {
                  this.$api.lane.getLaneObject(perContainerLaneId).then((r) => {
                    if (r.data?.id) _applyLane(r.data);
                  });
                }
              }).catch(() => {
                if (perContainerLaneId) {
                  this.$api.lane.getLaneObject(perContainerLaneId).then((r) => {
                    if (r.data?.id) _applyLane(r.data);
                  });
                }
              });
            } else {
              const prefillLaneId = perContainerLaneId || this.orderInfo?.laneId;
              if (prefillLaneId) {
                const match = this.laneList.find((l) => l.id === prefillLaneId);
                if (match) {
                  _applyLane(match);
                } else {
                  this.$api.lane.getLaneObject(prefillLaneId).then((r) => {
                    if (r.data?.id) _applyLane(r.data);
                  });
                }
              }
            }
          }
        })
        .finally(() => {
          this.laneLoading = false;
        });
    },
    fetchContainerTypes() {
      this.$api.containerType
        .getContainerTypeList({ limit: "all" })
        .then((res) => {
          this.containerTypeList = res.data;
          this.matchContainerTypes();
        });
    },
    fetchVehicles(params = {}) {
      this.vehicleLoading = true;
      this.$api.vehicle
        .getVehicleSelectList({ limit: 10, ...params })
        .then((res) => {
          this.vehicleList = params.offset
            ? [...this.vehicleList, ...res.data]
            : res.data;
          this.vehicleCount = res.count;
          this.prefillVehicle();
        })
        .finally(() => {
          this.vehicleLoading = false;
        });
    },
    fetchDrivers(params = {}) {
      this.driverLoading = true;
      this.$api.driver
        .getDriverSelectList({ limit: 10, ...params })
        .then((res) => {
          this.driverList = params.offset
            ? [...this.driverList, ...res.data]
            : res.data;
          this.driverCount = res.count;
          this.prefillDriver();
        })
        .finally(() => {
          this.driverLoading = false;
        });
    },
    fetchVendors(params = {}) {
      this.vendorLoading = true;
      this.$api.company
        .getCompanyList({ company_type: "Transporter", limit: 10, ...params })
        .then((res) => {
          const vendors = Array.isArray(res.data) ? res.data : [];
          this.vendorList = params.offset
            ? [...this.vendorList, ...vendors]
            : vendors;
          this.vendorCount = res.count || 0;
          if (!params.offset && this.orderInfo?.vendorId && !this.form.vendor) {
            const match = this.vendorList.find(
              (v) => v.id === this.orderInfo.vendorId,
            );
            if (match) {
              this.form.vendor = match;
            } else {
              this.$api.company
                .getCompanyObject(this.orderInfo.vendorId)
                .then((r) => {
                  if (r.data && r.data.id) {
                    this.vendorList = [r.data, ...this.vendorList];
                    this.form.vendor = r.data;
                  }
                });
            }
          }
        })
        .catch(() => { this.vendorList = []; })
        .finally(() => {
          this.vendorLoading = false;
        });
    },

    submit() {
      this.submitting = true;
      const getVal = (v) => (v && typeof v === "object" ? v.id : v);
      const payload = {
        customer: getVal(this.form.customer),
        lane: getVal(this.form.lane),
        is_sub_hire: this.form.is_sub_hire,
        vendor: this.form.is_sub_hire ? getVal(this.form.vendor) : null,
        vehicle: !this.form.is_sub_hire ? getVal(this.form.vehicle) : null,
        vehicle_number: this.form.vehicle_number,
        driver: !this.form.is_sub_hire ? getVal(this.form.driver) : null,
        driver_name: this.form.driver_name,
        containers: this.form.containers
          .filter((c) => c.container_no || c.container_type)
          .map((c) => ({
            container_no: c.container_no,
            container_type: c.container_type,
            lane_id: c.lane_id || null,
            customer_id: c.customer_id || null,
          })),
      };
      this.$api.emergencyOrder
        .portalCreateDA(this.orderId, payload)
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "DA created successfully!",
            color: "success",
          });
          this.$emit("daCreated");
          this.closeDialog();
        })
        .catch(console.error)
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.section-block {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
  border: 1px solid #e9ecef;
}
.section-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}
.container-row {
  background: white;
  border-radius: 6px;
  padding: 6px 8px;
  border: 1px solid #eeeeee;
}
.contract-status {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
  background: #f5f5f5;
  color: #424242;
}
.contract-status--none {
  border-color: #e0e0e0;
  background: #fafafa;
  color: #757575;
}
.contract-status--found {
  border-color: #c8e6c9;
  background: #f1f8f1;
  color: #2e7d32;
}
.contract-status--warn {
  border-color: #ffb74d;
  background: #fff8f0;
  color: #e65100;
}
.rate-chip--missing {
  background: #fff3e0 !important;
  color: #e65100 !important;
  border-color: #ffb74d !important;
}
.rate-chip {
  font-size: 11px;
  font-weight: 600;
  background: #e8f5e9;
  color: #388e3c;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #c8e6c9;
}
.info-chip {
  display: flex;
  flex-direction: column;
  padding: 6px 16px;
  border-right: 1px solid #e9ecef;
  min-width: 0;
}
.info-chip:last-child {
  border-right: none;
}
.info-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9e9e9e;
  font-weight: 500;
  line-height: 1.2;
}
.info-value {
  font-size: 12px;
  color: #424242;
  font-weight: 600;
  margin-top: 2px;
  white-space: nowrap;
}
</style>
